import React from "react";
import { format_date } from "../../components/CustomComponent";

// ชิ้นส่วน UI ที่ใช้ร่วมกันในระบบ "กินอะไรดี" (ธีมส้ม) คลาส .food-* อยู่ท้าย style.css

// หัวแผงเนื้อหาแบบเดียวกับหน้าอื่นในระบบ (ใช้ .stg-panel ของหน้าตั้งค่า)
export const Panel = ({ eyebrow, title, desc, icon, actions, children, className = "" }) => (
  <div className={`stg-panel ${className}`.trim()}>
    <div className="stg-panel-head">
      <div className="stg-panel-intro">
        <span className="stg-panel-icon"><i className={icon} /></span>
        <div style={{ minWidth: 0 }}>
          {eyebrow ? <p className="stg-eyebrow">{eyebrow}</p> : null}
          <h3 className="stg-panel-title">{title}</h3>
          {desc ? <p className="stg-panel-desc">{desc}</p> : null}
        </div>
      </div>
      {actions ? <div className="stg-panel-actions">{actions}</div> : null}
    </div>
    <div className="stg-panel-body">{children}</div>
  </div>
);

export const Section = ({ icon, title, desc, actions, children, className = "" }) => (
  <div className={`stg-section ${className}`.trim()}>
    <div className="stg-section-head">
      <span className="stg-section-mark"><i className={icon} /></span>
      <div style={{ minWidth: 0, flex: 1 }}>
        <h4 className="stg-section-title">{title}</h4>
        {desc ? <p className="stg-section-desc">{desc}</p> : null}
      </div>
      {actions}
    </div>
    {children}
  </div>
);

// แถบตัวเลขสรุป: items = [tone, icon, value, label, hint?]
export const KpiStrip = ({ items, className = "" }) => (
  <div className={`food-kpis ${className}`.trim()}>
    {items.map(([tone, icon, value, label, hint]) => (
      <div key={label} className={`food-kpi food-kpi--${tone}`} title={hint}>
        <span className="food-kpi-icon"><i className={icon} /></span>
        <div style={{ minWidth: 0 }}>
          <div className="food-kpi-value">{typeof value === "number" ? value.toLocaleString() : value}</div>
          <div className="food-kpi-label">{label}</div>
        </div>
      </div>
    ))}
  </div>
);

// แถบสารอาหารวันนี้ used / target
export const MacroBar = ({ label, icon, unit, tone, used, target, compact = false }) => {
  const percent = Math.min(100, Math.round((used / target) * 100));
  const over = used > target;
  return (
    <div className={`food-macro food-macro--${tone} ${compact ? "food-macro--compact" : ""} ${over ? "is-over" : ""}`.trim()}>
      <div className="food-macro-head">
        <span className="food-macro-icon"><i className={icon} /></span>
        <span className="food-macro-label">{label}</span>
        <span className="food-macro-value">
          <strong>{used.toLocaleString()}</strong> / {target.toLocaleString()} {unit}
        </span>
      </div>
      <div className="food-macro-track"><span className="food-macro-fill" style={{ width: `${percent}%` }} /></div>
      {!compact ? <div className="food-macro-note">{over ? `เกิน ${(used - target).toLocaleString()} ${unit}` : `เหลือ ${(target - used).toLocaleString()} ${unit}`}</div> : null}
    </div>
  );
};

// ป้ายเล็ก ๆ
export const Chip = ({ children, tone = "muted", icon, onClick, active = false, className = "" }) => {
  const Tag = onClick ? "button" : "span";
  return (
    <Tag type={onClick ? "button" : undefined} onClick={onClick} className={`food-chip food-chip--${tone} ${active ? "is-active" : ""} ${className}`.trim()}>
      {icon ? <i className={icon} /> : null}
      {children}
    </Tag>
  );
};

// รายการเหตุผลของ AI (✓ / ✕)
export const ReasonList = ({ reasons, light = false }) => (
  <ul className={`food-reasons ${light ? "food-reasons--light" : ""}`.trim()}>
    {reasons.map((reason) => {
      const item = typeof reason === "string" ? { ok: true, text: reason } : reason;
      return (
        <li key={item.text} className={item.ok ? "" : "is-no"}>
          <i className={item.ok ? "fa-solid fa-check" : "fa-solid fa-xmark"} />
          {item.text}
        </li>
      );
    })}
  </ul>
);

// สารอาหารสั้น ๆ ต่อท้ายชื่อเมนู
export const NutritionLine = ({ food, showPrice = true }) => (
  <span className="food-nutri">
    <span><i className="fa-solid fa-fire" />{food.kcal} kcal</span>
    <span><i className="fa-solid fa-drumstick-bite" />P {food.protein} g</span>
    <span><i className="fa-solid fa-bowl-rice" />C {food.carbs} g</span>
    <span><i className="fa-solid fa-seedling" />F {food.fat} g</span>
    {showPrice ? <span><i className="fa-solid fa-coins" />฿{food.price}</span> : null}
  </span>
);

// การ์ดเมนูใช้ในรายการทางเลือก
export const FoodCard = ({ food, rank, onClick, active = false, right }) => (
  <div className={`food-card ${active ? "is-active" : ""} ${onClick ? "is-clickable" : ""}`.trim()} onClick={onClick} role={onClick ? "button" : undefined}>
    {rank ? <span className={`food-rank food-rank--${rank}`}>{rank === 1 ? "🥇" : rank === 2 ? "🥈" : "🥉"}</span> : null}
    <span className="food-card-emoji">{food.emoji}</span>
    <div style={{ minWidth: 0, flex: 1 }}>
      <div className="food-card-name">{food.name}</div>
      <NutritionLine food={food} />
    </div>
    {right}
  </div>
);

// ปุ่มตอบกลับที่ระบบใช้เรียนรู้: 👍 ชอบ | 👎 ไม่เอา | 🔄 ขออีก | ❤️ เก็บไว้
export const FeedbackBar = ({ onLike, onDislike, onAnother, onSave, saved = false, liked = null }) => (
  <div className="food-feedback">
    <button type="button" className={`food-feedback-btn ${liked === true ? "is-active" : ""}`} onClick={onLike}><span>👍</span>ชอบ</button>
    <button type="button" className={`food-feedback-btn ${liked === false ? "is-active" : ""}`} onClick={onDislike}><span>👎</span>ไม่เอา</button>
    <button type="button" className="food-feedback-btn" onClick={onAnother}><span>🔄</span>ขออีก</button>
    <button type="button" className={`food-feedback-btn ${saved ? "is-active" : ""}`} onClick={onSave}><span>{saved ? "❤️" : "🤍"}</span>{saved ? "เก็บแล้ว" : "เก็บไว้"}</button>
  </div>
);

// รูปแบบวันที่ตามมาตรฐานของระบบ (format_date ใน CustomComponent, ปี ค.ศ.) เหมือนหน้า Welcome: "วันพุธ 24/09/2026"
export const thaiDate = (date = new Date()) => `${date.toLocaleDateString("th-TH", { weekday: "long" })} ${format_date(date, "dd/mm/yyyy")}`;

// ช่องกรอกแบบแท็ก (ชอบ / ไม่ชอบ / แพ้) พิมพ์แล้ว Enter เพื่อเพิ่ม ใช้ทั้งหน้าโปรไฟล์และคำถามถัดไปบนหน้าแรก
export const TagInput = ({ value = [], onChange, placeholder, tone = "orange", autoFocus = false }) => {
  const [text, setText] = React.useState("");
  const add = () => {
    const item = text.trim();
    if (!item || value.includes(item)) {
      setText("");
      return;
    }
    onChange([...value, item]);
    setText("");
  };
  return (
    <div className="food-taginput form-control">
      {value.map((item) => (
        <Chip key={item} tone={tone} className="food-pantry-chip">
          {item}
          <button type="button" className="food-chip-x" aria-label={`ลบ ${item}`} onClick={() => onChange(value.filter((tag) => tag !== item))}><i className="fa-solid fa-xmark" /></button>
        </Chip>
      ))}
      <input type="text" value={text} autoFocus={autoFocus} placeholder={value.length ? "" : placeholder} onChange={(event) => setText(event.target.value)} onKeyDown={(event) => { if (event.key === "Enter") { event.preventDefault(); add(); } }} onBlur={add} />
    </div>
  );
};

// มื้อตามเวลาปัจจุบัน (ตรงกับ getDaypart ของ backend): ใช้เป็นค่าเริ่มต้นตอนบันทึก "กินอันนี้"
export const currentMealKey = (hour = new Date().getHours()) => (hour < 10 ? "breakfast" : hour < 15 ? "lunch" : hour < 20 ? "dinner" : "late");

export const MEAL_LABELS = { breakfast: "มื้อเช้า", lunch: "มื้อกลางวัน", snack: "ของว่าง", dinner: "มื้อเย็น", late: "มื้อดึก" };
export const GROUP_LABELS = { rice: "ข้าว", noodle: "เส้น", meat: "เนื้อ", light: "เบา ๆ", snack: "ของว่าง", drink: "เครื่องดื่ม" };
export const SOURCE_LABELS = { home: "ทำเอง", restaurant: "ร้านอาหาร", delivery: "Delivery" };
export const SOURCE_ICONS = { home: "fa-solid fa-kitchen-set", restaurant: "fa-solid fa-store", delivery: "fa-solid fa-motorcycle" };

// GET ของระบบต่อ query จาก object ตรง ๆ (ไม่ encode) จึงส่งเฉพาะ key ที่มีค่า
export const compactQuery = (params) => {
  const query = Object.fromEntries(Object.entries(params || {}).filter(([, value]) => value !== undefined && value !== null && value !== ""));
  return Object.keys(query).length ? query : null;
};

export const todayIso = () => {
  const date = new Date();
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
};

export const shiftIso = (iso, days) => {
  const [year, month, day] = iso.split("-").map(Number);
  const date = new Date(year, month - 1, day + days);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
};

// แปลง "YYYY-MM-DD" (วันที่จาก backend) เป็นข้อความตามมาตรฐานของระบบ
// style: full = "วันพุธ 24/09/2026" | short = "24/09/2026" | day = "24 ก.ย." | weekday = "พ."
export const thaiDateOf = (iso, style = "full") => {
  const [year, month, day] = String(iso).split("-").map(Number);
  const date = new Date(year, month - 1, day);
  if (style === "weekday") return date.toLocaleDateString("th-TH", { weekday: "short" });
  if (style === "day") return format_date(date, "dd mmm");
  if (style === "short") return format_date(date, "dd/mm/yyyy");
  return thaiDate(date);
};

// การ์ดโครงตอนกำลังโหลด
export const Skeleton = ({ lines = 3 }) => (
  <div className="food-skeleton">
    {Array.from({ length: lines }).map((_, index) => <span key={index} style={{ width: `${90 - index * 15}%` }} />)}
  </div>
);
